import { useClientI18nModelStore } from '@/models/client-i18n/clientI18n';
import type { HttpApiServerType } from '@/services/http-api/httpApi';
import type { ResourceHttpApiData } from '@/shared/constant/ResourceData';
import { ParameterDisplayMode } from '@tarslib/types/lib/projectSetting';
import clsx from 'clsx';
import { RequestSampleCodeClassicsLanguages } from './components/request-sample-code-classics-languages/RequestSampleCodeClassicsLanguages';
import { RequestSampleCodeCode } from './components/request-sample-code-code/RequestSampleCodeCode';
import { RequestSampleCodeLanguages } from './components/request-sample-code-languages/RequestSampleCodeLanguages';
import { RequestSampleCodePrompt } from './components/request-sample-code-prompt/RequestSampleCodePrompt';
import { RequestSampleCodeVariants } from './components/request-sample-code-variants/RequestSampleCodeVariants';
import { useIsShowRequestSampleCode } from './hooks/useIsShowRequestSampleCode';
import { useSelectSampleCodeValue } from './hooks/useSelectSampleCodeValue';

export type RequestSampleCodeProps = {
  value: HttpApiServerType;
  extraData: ResourceHttpApiData['extraData'];
  mode?: ParameterDisplayMode;
  className?: string;
};

export function RequestSampleCode(props: RequestSampleCodeProps) {
  const { className, value, extraData, mode = ParameterDisplayMode.CLASSIC } = props;

  const isShowRequestSampleCode = useIsShowRequestSampleCode();
  const clientI18n = useClientI18nModelStore((x) => x.clientI18n);

  const {
    prompt,
    codeValue,
    codeBlockLanguage,
    selectedSampleCodeLanguage,
    selectedSampleCodeVariant,
    setSelectedSampleCodeLanguage,
    setSelectedSampleCodeVariant,
    selectedLanguageVariants,
    selectedRequestExample,
    requestExamples,
    setSelectedRequestKey,
  } = useSelectSampleCodeValue(value, extraData);

  if (!isShowRequestSampleCode || !value.codeSampleLanguages.length) {
    return null;
  }
  return (
    <div className={clsx('', className)}>
      {mode === ParameterDisplayMode.CLASSIC && (
        <h2 className="mb-4 text-xl font-600">{clientI18n('api.label.codeSample')}</h2>
      )}

      {mode === ParameterDisplayMode.CLASSIC && (
        <RequestSampleCodeClassicsLanguages
          className="flex"
          languages={value.codeSampleLanguages}
          value={selectedSampleCodeLanguage}
          onChange={setSelectedSampleCodeLanguage}
        />
      )}

      <div
        className={clsx('rounded-xl bg-code-viewer', {
          ['hidden']: mode === ParameterDisplayMode.CLASSIC && !selectedSampleCodeLanguage,
          ['flex h-full flex-col']: mode === ParameterDisplayMode.MODERN,
        })}
      >
        <RequestSampleCodeLanguages
          languages={value.codeSampleLanguages}
          value={selectedSampleCodeLanguage}
          onChange={setSelectedSampleCodeLanguage}
          className={clsx({
            ['hidden']: mode === ParameterDisplayMode.CLASSIC,
          })}
        />
        <div className="h-2" />
        <RequestSampleCodeVariants
          variants={selectedLanguageVariants}
          value={selectedSampleCodeVariant}
          onChange={setSelectedSampleCodeVariant}
          requestExamples={requestExamples}
          selectedRequestExampleKey={selectedRequestExample?.key}
          onSelectedRequestExampleKey={setSelectedRequestKey}
        />
        <RequestSampleCodePrompt value={prompt} />
        <RequestSampleCodeCode code={codeValue} language={codeBlockLanguage} mode={mode} />
      </div>
    </div>
  );
}
